import { AxiosResponse } from 'axios';
import { useMemo } from 'react';
import { useQueries, UseQueryResult } from 'react-query';
import { ConfigResponseType } from '../apis/ConfigAPI';
import { ConfigEntryType } from 'config/config';
import { getConfigEntryOptions, getConfigEntryQueryOptions } from '../helpers';

function useMultiConfigEntryOptionsQuery({
  companyId,
  configEntries,
  shouldFetch = true,
}: {
  companyId?: string;
  configEntries: {
    configEntry: ConfigEntryType;
    defaultOptions?: { label: string; value: string }[];
  }[];
  shouldFetch?: boolean;
}) {
  const queries = useQueries(
    configEntries.map((config) =>
      getConfigEntryQueryOptions({
        companyId,
        configEntry: config.configEntry,
        shouldFetch,
      }),
    ),
  );

  const { configsValues, isLoadingQueries } = useMemo(() => {
    const configsValues: Record<
      string,
      | {
          activeUniqueOrderedOptions: { label: string; value: string }[];
          queryResponse: UseQueryResult<AxiosResponse<ConfigResponseType>> | undefined;
        }
      | undefined
    > = {};
    let isLoadingQueries = false;

    // Consolidate each config with responses
    configEntries.forEach((config, index) => {
      configsValues[config.configEntry.entryCode] = {
        activeUniqueOrderedOptions: getConfigEntryOptions({
          ...config,
          data: queries[index].data?.data,
        }),
        queryResponse: config.configEntry.referenceObject?.functionName
          ? undefined
          : queries[index],
      };

      if (queries[index].isLoading) {
        isLoadingQueries = true;
      }
    });

    return {
      configsValues,
      isLoadingQueries,
    };
  }, [configEntries, queries]);

  return {
    configsValues,
    isLoadingQueries,
  };
}

export default useMultiConfigEntryOptionsQuery;
